import { Link, Outlet, useRouterState } from '@tanstack/react-router';
import { Card, CardContent, CardDescription, CardHeader, CardTitle, PageHeader, cn, segmentedItemClass, segmentedListClass } from '@lawbee/ui';
import type { ReactNode } from 'react';
import { can, hasFeature, useMe } from '../../lib/me';
import type { Me } from '../../lib/types';

type Section = { to: string; label: string; show?: (me: Me) => boolean; growth?: boolean };

const SECTIONS: Section[] = [
  { to: '/settings/firm', label: 'Firm' },
  { to: '/settings/team', label: 'Team' },
  { to: '/settings/billing', label: 'Billing' },
  { to: '/settings/matter-types', label: 'Matter types' },
  { to: '/settings/templates', label: 'Update templates' },
  { to: '/settings/booking', label: 'Booking' },
  { to: '/settings/payments', label: 'Payments' },
  { to: '/settings/privacy', label: 'Privacy and data' },
  { to: '/settings/integrations', label: 'Integrations', growth: true },
  { to: '/settings/audit', label: 'Audit log', show: (me) => can(me, 'audit') },
];

export function SettingsLayout() {
  const me = useMe();
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const sections = SECTIONS.filter((s) => !s.show || s.show(me));
  const isActive = (to: string) => pathname === to || pathname.startsWith(`${to}/`);

  return (
    <div className="mx-auto max-w-6xl">
      <PageHeader title="Settings" description="Your firm, team, plan and how LawBee works for you." />
      <div className="flex flex-col gap-6 lg:flex-row lg:gap-10">
        {/* Mobile and tablet: horizontal segmented nav that scrolls. */}
        <nav aria-label="Settings sections" className={cn(segmentedListClass, 'lg:hidden')}>
          {sections.map((s) => (
            <Link key={s.to} to={s.to} className={segmentedItemClass(isActive(s.to))} aria-current={isActive(s.to) ? 'page' : undefined}>
              {s.label}
            </Link>
          ))}
        </nav>
        {/* Desktop: vertical list. */}
        <nav aria-label="Settings sections" className="hidden w-52 shrink-0 lg:block">
          <ul className="sticky top-24 space-y-0.5">
            {sections.map((s) => {
              const active = isActive(s.to);
              return (
                <li key={s.to}>
                  <Link
                    to={s.to}
                    aria-current={active ? 'page' : undefined}
                    className={cn(
                      'flex h-9 items-center justify-between gap-2 rounded-lg px-3 text-[14px] transition-colors',
                      active ? 'bg-card font-semibold text-foreground shadow-card' : 'font-medium text-muted-foreground hover:bg-foreground/[0.04] hover:text-foreground',
                    )}
                  >
                    {s.label}
                    {s.growth && !hasFeature(me, 'integrations') ? <span className="text-[11px] font-medium text-muted-foreground">Growth</span> : null}
                  </Link>
                </li>
              );
            })}
          </ul>
        </nav>
        <div className="min-w-0 flex-1">
          <Outlet />
        </div>
      </div>
    </div>
  );
}

/** Card for one settings group. */
export function SettingsCard({ title, description, children, footer, className }: { title: ReactNode; description?: ReactNode; children: ReactNode; footer?: ReactNode; className?: string }) {
  return (
    <Card className={className}>
      <CardHeader>
        <CardTitle>{title}</CardTitle>
        {description ? <CardDescription>{description}</CardDescription> : null}
      </CardHeader>
      <CardContent>{children}</CardContent>
      {footer ? <div className="flex flex-wrap items-center justify-end gap-2 border-t border-border/70 px-6 py-4">{footer}</div> : null}
    </Card>
  );
}
